import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { useAuth } from "@/context/AuthContext";
import api, { formatErr } from "@/lib/api";
import { PageHeader, Section, StatusBadge } from "@/components/Shared";
import { toast } from "sonner";
import { FileXls, FilePdf, FileCsv } from "@phosphor-icons/react";

async function downloadWeek(wid, format, filename) {
  const r = await fetch(`${process.env.REACT_APP_BACKEND_URL}/api/timesheets/weeks/${wid}/export?format=${format}`, {
    credentials: "include",
  });
  if (!r.ok) {
    const txt = await r.text();
    throw new Error(txt || "Export failed");
  }
  const blob = await r.blob();
  const url = URL.createObjectURL(blob);
  const a = document.createElement("a");
  a.href = url; a.download = filename; a.click();
  URL.revokeObjectURL(url);
}

function WeekRow({ w, expanded, setExpanded }) {
  const { user } = useAuth();
  const isSuper = user?.role === "super_admin";
  const open = expanded === w.id;
  const safeName = (w.user_name || "employee").replace(/[^A-Za-z0-9_-]+/g, "_");
  const doExport = async (fmt) => {
    try { await downloadWeek(w.id, fmt, `week_${w.week_start}_${safeName}.${fmt}`); }
    catch (e) { toast.error(e.message || "Export failed"); }
  };
  return (
    <>
      <tr className="border-b border-[#F0F0EE] cursor-pointer hover:bg-[#F7F7F5]" onClick={() => setExpanded(open ? null : w.id)}>
        <td className="font-medium" data-testid={`week-employee-${w.id}`}>{w.user_name}</td>
        <td className="whitespace-nowrap">{w.week_start} → {w.week_end}</td>
        <td className="font-head font-semibold">{w.total_hours}h</td>
        <td>{w.billable_hours}h</td>
        <td>{w.days_count}</td>
        <td><StatusBadge status={w.status} direct={!w.reviewed_by} /></td>
        <td>
          <div className="flex gap-2 items-center" onClick={(e) => e.stopPropagation()}>
            {w.status === "submitted" && isSuper && (
              <span className="text-xs text-neutral-500 mr-1" data-testid={`week-awaiting-team-admin-${w.id}`}>Awaiting Team Admin approval</span>
            )}
            {w.status === "admin_approved" && !isSuper && (
              <span className="text-xs text-neutral-500 mr-1" data-testid={`week-awaiting-super-${w.id}`}>Awaiting Super Admin approval</span>
            )}
            {((w.status === "submitted" && !isSuper) || (w.status === "admin_approved" && isSuper)) && (
              <Link to="/admin/approvals" className="text-xs font-semibold text-[#1F4E8C] underline underline-offset-2 mr-1"
                    data-testid={`week-goto-approvals-${w.id}`}>Review in Pending Approvals</Link>
            )}
            {w.status === "rejected" && w.rejection_reason && (
              <div className="text-xs text-red-600 max-w-[180px] truncate" title={w.rejection_reason}>
                Rejected
              </div>
            )}
            {w.status === "approved" && (
              <div className="text-xs text-neutral-500 mr-1">by {w.reviewed_by || "Admin"}</div>
            )}
            <button onClick={() => doExport("xlsx")} title="Download XLSX"
                    data-testid={`export-xlsx-${w.id}`}
                    className="inline-flex items-center gap-1 px-2 h-8 border border-[#1F4E8C] text-[#1F4E8C] text-xs hover:bg-[#1F4E8C] hover:text-white transition-colors">
              <FileXls size={14} /> XLSX
            </button>
            <button onClick={() => doExport("csv")} title="Download CSV"
                    data-testid={`export-csv-${w.id}`}
                    className="inline-flex items-center gap-1 px-2 h-8 border border-emerald-600 text-emerald-700 text-xs hover:bg-emerald-600 hover:text-white transition-colors">
              <FileCsv size={14} /> CSV
            </button>
            <button onClick={() => doExport("pdf")} title="Download PDF"
                    data-testid={`export-pdf-${w.id}`}
                    className="inline-flex items-center gap-1 px-2 h-8 border border-[#FF5500] text-[#FF5500] text-xs hover:bg-[#FF5500] hover:text-white transition-colors">
              <FilePdf size={14} /> PDF
            </button>
          </div>
        </td>
      </tr>
      {open && (
        <tr><td colSpan={7} className="bg-[#F7F7F5] p-4">
          <div className="mb-4 flex flex-wrap gap-3 text-xs">
            <div className="px-3 py-1.5 bg-white border border-[#E5E5E5]">
              <span className="text-neutral-500">Overtime:</span> <strong className="text-[#FF5500]">{w.overtime_hours ?? 0}h</strong>
            </div>
            {!!w.weekend_hours && (
              <div className="px-3 py-1.5 bg-white border border-[#E5E5E5]">
                <span className="text-neutral-500">Weekend:</span> <strong>{w.weekend_hours}h</strong>
              </div>
            )}
            {!!w.holiday_hours && (
              <div className="px-3 py-1.5 bg-white border border-[#E5E5E5]">
                <span className="text-neutral-500">Holiday:</span> <strong>{w.holiday_hours}h</strong>
              </div>
            )}
            {!!w.comp_off_days_earned && (
              <div className="px-3 py-1.5 bg-[#FFF4EC] border border-[#FF5500]">
                <span className="text-neutral-700">Comp-off eligible:</span> <strong className="text-[#FF5500]">{w.comp_off_days_earned} day{w.comp_off_days_earned === 1 ? "" : "s"}</strong>
                <span className="text-neutral-500"> (banked on approval)</span>
              </div>
            )}
          </div>
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
            <div>
              <div className="eyebrow mb-2">Per project (week total)</div>
              <table className="w-full text-xs">
                <thead><tr className="text-left border-b border-[#E5E5E5]"><th className="eyebrow">Project</th><th className="eyebrow">Hours</th><th className="eyebrow">Billable</th></tr></thead>
                <tbody>
                  {(w.per_project || []).map((p) => (
                    <tr key={p.project_id} className="border-b border-[#E5E5E5]">
                      <td className="font-medium">{p.project_name}</td>
                      <td>{p.hours}h</td>
                      <td>{p.billable_hours}h</td>
                    </tr>
                  ))}
                  {(w.per_project || []).length === 0 && <tr><td colSpan={3} className="py-3 text-neutral-500">No project entries</td></tr>}
                </tbody>
              </table>
            </div>
            <div>
              <div className="eyebrow mb-2">Daily breakdown</div>
              <table className="w-full text-xs">
                <thead><tr className="text-left border-b border-[#E5E5E5]"><th className="eyebrow">Date</th><th className="eyebrow">Hours</th><th className="eyebrow">Billable</th><th className="eyebrow">Tags</th></tr></thead>
                <tbody>
                  {(w.days || []).map((d) => (
                    <tr key={d.date} className="border-b border-[#E5E5E5]">
                      <td>{d.date}<span className="text-neutral-500"> · {d.weekday}</span></td>
                      <td>{d.total_hours}h{d.overtime_hours > 0 && <span className="text-[#FF5500]"> (+{d.overtime_hours} OT)</span>}</td>
                      <td>{d.billable_hours}h</td>
                      <td>
                        {d.is_holiday && <span className="mr-1 px-1.5 py-0.5 bg-red-50 border border-red-300 text-red-700">Holiday</span>}
                        {d.is_weekend && !d.is_holiday && <span className="mr-1 px-1.5 py-0.5 bg-amber-50 border border-amber-300 text-amber-800">Weekend</span>}
                        {d.comp_off_eligible && <span className="px-1.5 py-0.5 bg-[#FFF4EC] border border-[#FF5500] text-[#FF5500]">+1 comp-off</span>}
                      </td>
                    </tr>
                  ))}
                  {(w.days || []).length === 0 && <tr><td colSpan={4} className="py-3 text-neutral-500">No daily entries logged</td></tr>}
                </tbody>
              </table>
            </div>
          </div>
        </td></tr>
      )}
    </>
  );
}

export default function AdminTimesheets() {
  const [weeks, setWeeks] = useState([]);
  const [status, setStatus] = useState("all");
  const [expanded, setExpanded] = useState(null);

  useEffect(() => {
    const q = status === "all" ? "" : `?status=${status}`;
    api.get(`/timesheets/weeks${q}`).then((r) => setWeeks(r.data)).catch((e) => toast.error(formatErr(e)));
  }, [status]);

  const label = status === "admin_approved" ? "admin approved" : status;

  return (
    <div data-testid="admin-timesheets">
      <PageHeader overline="Records" title="Timesheet History"
        subtitle="Weekly timesheets with project summaries and exports. Approve or reject them in Pending Approvals."
        actions={
          <select value={status} onChange={(e) => setStatus(e.target.value)}
                  className="h-10 border border-[#E5E5E5] px-3 bg-white"
                  data-testid="ts-status-filter">
            {["all", "submitted", "admin_approved", "approved", "rejected"].map((s) => <option key={s} value={s}>{s === "admin_approved" ? "admin approved" : s}</option>)}
          </select>
        } />

      <Section title={`${weeks.length} week${weeks.length === 1 ? "" : "s"} · ${label}`}>
        <div className="overflow-x-auto -mx-5 sm:mx-0">
          <table className="w-full table-dense min-w-[720px]">
            <thead>
              <tr className="border-b-2 border-[#1F4E8C] text-left">
                <th className="eyebrow">Employee</th>
                <th className="eyebrow">Week</th>
                <th className="eyebrow">Total</th>
                <th className="eyebrow">Billable</th>
                <th className="eyebrow">Days</th>
                <th className="eyebrow">Status</th>
                <th className="eyebrow">Actions</th>
              </tr>
            </thead>
            <tbody>
              {weeks.map((w) => (
                <WeekRow key={w.id} w={w} expanded={expanded} setExpanded={setExpanded} />
              ))}
              {weeks.length === 0 && (
                <tr><td colSpan={7} className="text-center py-8 text-neutral-500">No {label} weekly timesheets</td></tr>
              )}
            </tbody>
          </table>
        </div>
      </Section>
    </div>
  );
}
